import { MONTHS_SHORT_NAMES } from "../../../../../constants/dates";
import { formatNumber } from "../../dashboard-utils";
import { Cell, ChartData, ChartItem, Row } from "../../clients-dashboard-types";
import { TableProps } from "../../common-components/table/table-types";
import {
  IGetUnpaidInvoicesData,
  Month,
} from "../../../../../services/ClientsDashboardServices.dto";

const sortYears = (data: IGetUnpaidInvoicesData[]): IGetUnpaidInvoicesData[] => {
  return [...data].sort((a, b) => parseInt(a.year, 10) - parseInt(b.year, 10));
};

const sortMonths = (year: IGetUnpaidInvoicesData): Month[] => {
  return [...year.monthlyUnpaidReceipts].sort(
    (a, b) => parseInt(a.month, 10) - parseInt(b.month, 10)
  );
};

const getMonthsArray = (years: IGetUnpaidInvoicesData[]): (Month & { year: string })[] => {
  return years.flatMap((year) =>
    sortMonths(year).map((month) => ({
      ...month,
      year: year.year,
    }))
  );
};

export const getTableData = (data: IGetUnpaidInvoicesData[]): TableProps => {
  const headerCells: Cell[] = [
    { className: "", value: "Productos" },
    { className: "header", value: "Impagados" },
  ];
  const headRow: Row = { className: "", cells: headerCells };
  const bodyRows: Row[] = [];

  const sortedYears = sortYears(data);

  sortedYears.forEach((year) => {
    const yearRow: Row = {
      className: "year",
      cells: [
        { className: "", value: year.year, contractActionList: [`${year.year}`] },
        { className: "", value: "" },
      ],
    };
    bodyRows.push(yearRow);

    const monthsRow: Row[] = [];
    const sortedMonths = sortMonths(year);
    sortedMonths.forEach((month) => {
      const monthShortName = MONTHS_SHORT_NAMES[month.month];
      const monthRow: Row = {
        className: "month strong",
        cells: [
          {
            className: "strong",
            value: monthShortName,
            contractActionList: [`${year.year}${monthShortName}`],
          },
          { className: "number strong", value: formatNumber(month.monthTotalImport) },
        ],
        contractByList: [`${year.year}`],
      };
      monthsRow.push(monthRow);

      month.unpaidReceiptsByProductType.forEach((product) => {
        const productRow: Row = {
          className: "product",
          cells: [
            { className: "", value: product.productType },
            { className: "number", value: formatNumber(product.totalImport) },
          ],
          contractByList: [`${year.year}`, `${year.year}${monthShortName}`],
        };
        monthsRow.push(productRow);
      });
    });
    bodyRows.push(...monthsRow);
  });

  return {
    headRow,
    bodyRows,
  };
};

export const getUnpaidChartData = (data: IGetUnpaidInvoicesData[]): ChartData => {
  const sortedYears = sortYears(data);
  const monthsArray = getMonthsArray(sortedYears);

  // El Chart necesita el nombre de todos los productos
  const productTypes = [
    ...new Set(
      monthsArray.flatMap((month) =>
        month.unpaidReceiptsByProductType.map((product) => product.productType)
      )
    ),
  ];

  const chartData: ChartData = monthsArray.map((month) => {
    const chartItem: ChartItem = {
      month: MONTHS_SHORT_NAMES[month.month],
      year: month.year,
    };

    // Inicializamos todos los productos a 0
    // para mejorar la visualización del Chart de líneas
    productTypes.forEach((productType) => {
      chartItem[productType] = 0;
    });

    // Añadimos los valores existentes
    month.unpaidReceiptsByProductType.forEach((product) => {
      chartItem[product.productType] = product.totalImport;
    });

    return chartItem;
  });

  return chartData;
};
